// src/features/purchasing2/api.js
import axios from 'axios';

const API_BASE = process.env.REACT_APP_SERVER_URL || '';

// Helper function for making API calls
async function apiCall(method, endpoint, data) {
  const config = {
    method,
    url: `${API_BASE}/api/v1/purchasing2${endpoint}`,
    headers: {
      'Content-Type': 'application/json',
    },
    ...(data && { data }),
  };

  const response = await axios(config);
  return response.data;
}

// Helper functions
export function generateIdempotencyKey() {
  return `idem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function getStatusColor(status) {
  const colors = {
    DRAFT: 'bg-gray-100 text-gray-800',
    READY_TO_SEND: 'bg-yellow-100 text-yellow-800',
    SENT: 'bg-blue-100 text-blue-800',
    PARTIALLY_RECEIVED: 'bg-orange-100 text-orange-800',
    RECEIVED: 'bg-green-100 text-green-800',
    CLOSED: 'bg-gray-100 text-gray-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getStatusLabel(status) {
  const labels = {
    DRAFT: 'Draft',
    READY_TO_SEND: 'Ready to Send',
    SENT: 'Sent',
    PARTIALLY_RECEIVED: 'Partially Received',
    RECEIVED: 'Received',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled',
  };
  return labels[status] || status;
}

// API functions
export const Purchasing2Api = {
  // Bootstrap data
  async getBootstrap() {
    return apiCall('GET', '/bootstrap');
  },

  // Cart operations
  async getCartItems() {
    return apiCall('GET', '/cart');
  },

  async addToCart(itemId, quantity) {
    return apiCall('POST', '/cart/add', { itemId, quantity });
  },

  async removeFromCart(cartItemId) {
    return apiCall('DELETE', '/cart/remove', { cartItemId });
  },

  async getPurchasingItems(searchTerm, page = 1, limit = 10) {
    const params = new URLSearchParams();
    if (searchTerm) params.append('searchParam', searchTerm);
    params.append('page', page);
    params.append('limit', limit);
    return apiCall('GET', `/purchasing-items?${params.toString()}`);
  },

  // Category operations
  async getCategories() {
    return apiCall('GET', '/categories');
  },

  async getParentCategories() {
    return apiCall('GET', '/categories/parents');
  },

  async getItemsByCategory(categoryId, searchTerm) {
    const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
    return apiCall('GET', `/categories/${categoryId}/items${params}`);
  },

  // Purchase Order operations
  async createFromCart(cartId) {
    return apiCall('POST', '/po/from-cart', { cartId });
  },

  async prepare(poId) {
    return apiCall('PUT', `/po/${poId}/prepare`, {});
  },

  async send(poId, method, idempotencyKey) {
    return apiCall('POST', `/po/${poId}/send`, {
      method,
      idempotencyKey,
    });
  },

  async receive(poId, items) {
    return apiCall('POST', `/po/${poId}/receive`, { items });
  },

  async close(poId, reason) {
    return apiCall('PUT', `/po/${poId}/close`, { reason });
  },

  async getDetails(poId) {
    return apiCall('GET', `/po/${poId}`);
  },

  async getStatusHistory(poId) {
    return apiCall('GET', `/po/${poId}/status-history`);
  },

  async getEventHistory(poId) {
    return apiCall('GET', `/po/${poId}/event-history`);
  },
};
